import React, { useEffect, useMemo, useState } from 'react';
import { FilterSideBar, useDebounce } from '@ole-ui/ole-ui-components';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import useMobxStores from 'hooks/useMobxStores';
import ComponentError from 'components/Error/ComponentError';
import ExploreSubscriptions from 'components/ExploreSubscriptions';
import { handleError } from 'services/ErrorService';

import './styles.scss';
import {
  Button,
  FormGroup,
  Text,
  TextInput,
  TextVariants,
} from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';

// this component is almost a copy of src/components/Catalog/Filters/index.tsx
// this only exists temporarily until the two views are merged in the future
const LearningPathsFilters = () => {
  const { t } = useTranslation();
  const { learningPathStore, userStore, searchStore, routerStore } =
    useMobxStores();

  const [searchSubstring, setSearchSubstring] = useState(
    (routerStore.route as any)?.params?.q || '',
  );
  const debouncedSearchSubstring = useDebounce<string>(searchSubstring, 300);

  const filterConfigs = learningPathStore.getFilterConfigs();

  const filterGroups = useMemo(
    () =>
      filterConfigs.map((filterConfig) => ({
        title: filterConfig.title,
        groupKey: filterConfig.key,
        'data-analytics-id': `${filterConfig.key}-filt-skilp-lp`,
        hasError: Boolean(
          learningPathStore.filterInfoErrors?.[filterConfig.key],
        ),
        options:
          learningPathStore.filters[filterConfig.key]?.map((option) => {
            const optionAnalyticsId = `${option.key}-${filterConfig.key}-filt-skilp-lp`;

            return {
              ...option,
              title: option.value,
              navItemProps: {
                'data-analytics-id': `container-${optionAnalyticsId}`,
              },
              checkboxProps: {
                'data-analytics-id': optionAnalyticsId,
              },
            };
          }) || [],
        mergeIdenticalLabels: filterConfig.groupIdenticalLabels,
      })),
    [
      filterConfigs,
      learningPathStore.filters,
      learningPathStore.filterInfoErrors,
    ],
  );

  const allItemsFailed =
    filterGroups.length > 0 && filterGroups.every((fg) => fg.hasError);

  const handleFilterSelect = (groupKey: string, option: string) => {
    learningPathStore.toggleFilterOption(groupKey, option);
    learningPathStore.recalculateFilterCounts();
  };

  useEffect(() => {
    const trimmed = debouncedSearchSubstring?.trim?.();
    searchStore.query = trimmed || ''; // update search query

    if (routerStore.route) {
      const routeParams = (routerStore.route as any)?.params || {};
      routerStore.navigate(
        routerStore.route.name,
        {
          ...routeParams,
          q: trimmed || undefined,
        },
        // @ts-ignore // this is a bug in the types
        { replace: true },
      ); // update query param
    }

    if (trimmed !== '') {
      learningPathStore.filterEntries(); // filter entries
    }
  }, [debouncedSearchSubstring]);

  const handleSearch = (_event: unknown, value: string) => {
    setSearchSubstring(value);
  };

  useEffect(() => {
    if ((routerStore.route as any)?.params?.q) {
      handleSearch(null, (routerStore.route as any).params.q);
    }
  }, [(routerStore.route as any)?.params?.q]);

  return (
    <FilterSideBar
      expandedGroups={learningPathStore.expandedFilterGroups}
      onGroupExpand={learningPathStore.toggleFilterGroups}
      onFilterSelect={handleFilterSelect}
      aria-label={t('Filter the list of skills paths')}
      allItemsFailed={allItemsFailed}
    >
      <div className="lp-filters__header">
        <Text className="lp-filters__title" component={TextVariants.h3}>
          {t('Skills paths')}
        </Text>
        <ExploreSubscriptions className="lp-filters__howtobuy" />
      </div>
      <FilterSideBar.NavGroup title={t('Filters')}>
        <FilterSideBar.ActiveFilterLabels groups={filterGroups} numLabels={5} />
        <div className="lp-filters__search">
          <FormGroup label={t('Search')} type="string">
            <TextInput
              className="lp-filters__search-input"
              data-analytics-id="text-filter-filt-skillp-lp"
              value={searchSubstring}
              placeholder={t('Search skills paths by title')}
              onChange={handleSearch}
              aria-label={t('Search skills paths')}
              maxLength={50}
              onFocus={(e) => {
                e.target?.select?.();
              }}
            />
          </FormGroup>
        </div>
        {filterGroups.map((fg) => (
          <FilterSideBar.FilterGroup key={fg.groupKey} {...fg} />
        ))}
      </FilterSideBar.NavGroup>
      {(userStore.currentSubscriptionCode ||
        userStore.isOrganizationAdmin ||
        userStore.isOrganizationManager) && (
        <div className="lp-filters__custom-filters">
          <Button
            data-analytics-id="create-custom-btn-skillp-lp"
            className="lp-filters__create-custom-btn"
            key="lp-csk-button"
            onClick={() => {
              routerStore.navigate('custom-skill-path');
            }}
          >
            {t('Create Custom Skill Path')}
          </Button>
        </div>
      )}
    </FilterSideBar>
  );
};

export default withErrorBoundary(
  observer(LearningPathsFilters),
  ComponentError,
  handleError,
);
